<script setup lang="ts">
import type { FormSubmitEvent } from "@nuxt/ui";

import { reactive, ref } from "vue";

import { useRouter } from "vue-router";
import { z } from "zod";

import { client } from "@/api";
import BrokerSelect from "@/components/BrokerSelect.vue";
import UnderwriterSelect from "@/components/UnderwriterSelect.vue";
import { useAuthStore } from "@/stores/auth";

const open = ref(false);

const schema = z.object({
	tailNumber: z.string().min(1, "Tail number is required"),
	make: z.string().min(1, "Make is required"),
	model: z.string().min(1, "Model is required"),
	year: z.string().min(1, "Year is required"),
	createLead: z.boolean(),
	contactName: z.string().optional(),
	contactEmail: z.string().optional(),
	contactPhone: z.string().optional(),
	broker: z.string().nullable().optional(),
	policyExpirationDate: z.string().optional(),
	assignedUnderwriter: z.string().nullable().optional(),
	notes: z.string().optional(),
})
	// Contact fields are hidden unless the box is checked; validating them otherwise
	// blocks submit on an error the user can't see or fix.
	.refine(
		(data) => !data.createLead || !data.contactEmail || z.string().email().safeParse(data.contactEmail).success,
		{ message: "Invalid email", path: ["contactEmail"] },
	);
type Schema = z.output<typeof schema>;

const authStore = useAuthStore();

const state = reactive<Partial<Schema> & { createLead: boolean }>({
	tailNumber: undefined,
	make: undefined,
	model: undefined,
	year: undefined,
	createLead: false,
	contactName: "",
	contactEmail: "",
	contactPhone: "",
	broker: null,
	policyExpirationDate: "",
	assignedUnderwriter: authStore.session?.id ?? null,
	notes: "",
});

const props = withDefaults(defineProps<{
	onSubmit?: (payload: {
		tailNumber: string;
		make: string;
		model: string;
		year: string;
	}) => Promise<void>;
	title?: string;
	buttonLabel?: string;
}>(), {
	title: "Add an aircraft",
	buttonLabel: "Create Aircraft",
	onSubmit: undefined,
});

const toast = useToast();
const router = useRouter();

const findOrCreateLead = async (tailNumber: string) => {
	const resp = await client.api.v1.leads["for-aircraft"].post({
		tailNumber,
		contactName: state.contactName || null,
		contactEmail: state.contactEmail || null,
		contactPhone: state.contactPhone || null,
		broker: state.broker || null,
		policyExpirationDate: state.policyExpirationDate || null,
		assignedUnderwriter: state.assignedUnderwriter ?? null,
		notes: state.notes || null,
	});
	if (!resp.data?.lead) {
		toast.add({
			title: "Error", description: "Failed to check or create a contact for this aircraft.", color: "error",
		});
		return;
	}
	const { lead, created } = resp.data;
	toast.add({
		title: created ? "Contact created" : "Existing contact already present",
		description: created
			? `New contact created for ${lead.aircraftRegistration}.`
			: `A contact for ${lead.aircraftRegistration} already exists.`,
		color: created ? "success" : "info",
		actions: [{
			label: "View contact", to: `/leads/${lead.id}`, color: "neutral", variant: "outline",
		}],
	});
};

const defaultOnSubmit = async (payload: {
	tailNumber: string;
	make: string;
	model: string;
	year: string;
}) => {
	const result = await client.api.v1.admin.aircraft.post({
		tailNumber: payload.tailNumber,
		make: payload.make,
		model: payload.model,
		year: payload.year,
	});
	if (result.status === 409) {
		toast.add({
			title: "Error", description: "Aircraft already exists with this tail number.", color: "error", 
		});
		return;
	}
	if (result.status !== 200) {
		toast.add({
			title: "Error", description: "Failed to create aircraft.", color: "error", 
		});
		return;
	}

	toast.add({
		title: "Success", description: `${payload.tailNumber} has been created.`, color: "success",
	});
	if (state.createLead) {
		await findOrCreateLead(payload.tailNumber);
	}
	router.push(`/aircraft/${payload.tailNumber}`);

};


async function handleSubmit (event: FormSubmitEvent<Schema>) {
	if (props.onSubmit) {
		await props.onSubmit(event.data);
	} else {
		await defaultOnSubmit(event.data);
	}
	state.make = undefined;
	state.model = undefined;
	state.year = undefined;
	state.tailNumber = undefined;
	state.createLead = false;
	state.contactName = "";
	state.contactEmail = "";
	state.contactPhone = "";
	state.broker = null;
	state.policyExpirationDate = "";
	state.assignedUnderwriter = authStore.session?.id ?? null;
	state.notes = "";
	open.value = false;
}

defineExpose({
	openModal: () => {
		open.value = true;
	},
});
</script>

<template>
  <UModal
    v-model:open="open"
    :title="title"
  >
    <slot />
    <template #body>
      <UForm
        :state="state"
        :schema="schema"
        @submit="handleSubmit"
      >
        <UFormField
          label="Tail Number"
          name="tailNumber"
        >
          <UInput v-model="state.tailNumber" />
        </UFormField>
        <UFormField
          label="Year"
          name="year"
        >
          <UInput
            v-model="state.year"
          />
        </UFormField>

        <UFormField
          label="Make"
          name="make"
        >
          <UInput
            v-model="state.make"
          />
        </UFormField>
        <UFormField
          label="Model"
          name="model"
        >
          <UInput
            v-model="state.model"
          />
        </UFormField>

        <UCheckbox
          v-if="!props.onSubmit"
          v-model="state.createLead"
          class="mt-4"
          label="Also create a contact for this aircraft"
        />

        <template v-if="!props.onSubmit && state.createLead">
          <UFormField
            label="Contact Name"
            name="contactName"
          >
            <UInput
              v-model="state.contactName"
              placeholder="Optional"
              class="w-full"
            />
          </UFormField>
          <UFormField
            label="Email"
            name="contactEmail"
          >
            <UInput
              v-model="state.contactEmail"
              type="email"
              placeholder="Optional"
              class="w-full"
            />
          </UFormField>
          <UFormField
            label="Phone"
            name="contactPhone"
          >
            <UInput
              v-model="state.contactPhone"
              placeholder="Optional"
              class="w-full"
            />
          </UFormField>
          <UFormField
            label="Broker"
            name="broker"
          >
            <BrokerSelect
              :broker-value="state.broker"
              clearable
              @update:broker-value="(v: string | undefined) => state.broker = v ?? null"
            />
          </UFormField>
          <UFormField
            label="Current Policy Expiration"
            name="policyExpirationDate"
          >
            <UInput
              v-model="state.policyExpirationDate"
              type="date"
              class="w-full"
            />
          </UFormField>
          <UFormField
            label="Assigned Underwriter"
            name="assignedUnderwriter"
          >
            <UnderwriterSelect
              :underwriter-value="state.assignedUnderwriter"
              clearable
              @update:underwriter-value="(v: string | undefined) => state.assignedUnderwriter = v ?? null"
            />
          </UFormField>
          <UFormField
            label="Notes"
            name="notes"
          >
            <UTextarea
              v-model="state.notes"
              :rows="3"
              placeholder="Optional"
              class="w-full"
            />
          </UFormField>
        </template>

        <UButton
          class="mt-8"
          type="submit"
          :label="buttonLabel"
        />
      </UForm>
    </template>
  </UModal>
</template>